import * as _ from 'lodash'

import { EntityDataStore } from '@skinsoft/entity-data-store'
import { TranslatedString } from '@skinsoft/i18n'
import { Observable } from 'rxjs'
import { filter, map, pluck, switchMap } from 'rxjs/operators'
import { Item, ItemDataStore } from '../item/item-data-store'
import { CONTAINER_TYPES_FOR_SEARCH } from '@skinsoft/data-store'

export interface Container {
    id: string
    containerItemSchema: {
        name: TranslatedString
        openContainer: boolean
    },
    locationItemSchema?: {
        realTimeLocation?: string,
        permanentLocation?: string,
    }
    type: string
}

export enum ContainerStatus {
    OPEN = 'true',
    CLOSED = 'false',
    NOT_DEFINED = 'is-missing',
}

declare const ITEM_SEARCH_TYPES

export class ContainerDataStore extends EntityDataStore<Container> {
    public static getInstance() {
        if (!ContainerDataStore.instance) {
            ContainerDataStore.instance = new ContainerDataStore()
        }

        return ContainerDataStore.instance
    }

    public static install(Vue) {
        Vue.prototype.$containerDataStore = ContainerDataStore.getInstance()
    }

    private static instance: ContainerDataStore = null as any

    private static fragmentParser(response) {
        return response._embedded
            ? (response._embedded['numeric-container-items'] || []).concat(
                  response._embedded['physical-container-items'] || [],
              )
            : []
    }

    constructor() {
        super(
            'physical-container-items',
            undefined,
            undefined,
            undefined,
            undefined,
            ContainerDataStore.fragmentParser,
        )
    }

    public fetchAll(options): Observable<Container[]> {
        return super.fetchAll({ endpoint: '/container-items', ...options })
    }

    public addItem(container: Container, itemId: Item['id']) {
        return ItemDataStore.getInstance()
            .fetchOne(itemId)
            .pipe(
                map((item) => {
                    const actualContainers =
                        _.get(item, 'conditioningItemSchema.actualContainer', []) || []
                    actualContainers.push(container.id)
                    _.set(item, 'conditioningItemSchema.actualContainer', actualContainers)
                    return item
                }),
                switchMap((item) => ItemDataStore.getInstance().update(item.id, item)),
                filter(_.identity),
            )
    }

    public removeItem(containerId: string, item: Item) {
        _.set(
            item,
            'conditioningItemSchema.actualContainer',
            _.get(item, 'conditioningItemSchema.actualContainer').filter(
                (container) => container !== containerId,
            ),
        )
        return ItemDataStore.getInstance().update(item.id, item).pipe(filter(_.identity))
    }

    public getAssociatedItems(containerId: string, searchTypes: string[] = ITEM_SEARCH_TYPES) {
        return ItemDataStore.getInstance()
            .searchAll(null, null, 50, searchTypes, 0, [
                {
                    leftCriteria: {
                        entityField: '5baccdc870aa490001801e2f',
                        operator: 'equals',
                        type: 'criteria',
                        value: {
                            completeSearch: false,
                            refId: containerId,
                            type: 'ref',
                        },
                    },
                    linkOperator: null,
                    rightCriteria: null,
                    type: 'requestSearch',
                },
            ])
            .pipe(
                pluck('allData'),
                map((items) =>
                    items.map((item) =>
                        ItemDataStore.getInstance().fetchOne(
                            _.get(item, 'id') || _.get(item, 'item.id'),
                        ),
                    ),
                ),
            )
    }

    public searchAmongStatusList(
        searchText: string,
        pageSize: number,
        page: number,
        statusList: ContainerStatus[],
        orderingFields?: { entityFields: string[], sortAsc: boolean },
        excludedIds?: string[]
    ) {
        return this.searchAll(
            searchText,
            orderingFields,
            pageSize,
            CONTAINER_TYPES_FOR_SEARCH,
            page,
            [
                statusList
                    .map((apiStatus) => ({
                        leftCriteria: {
                            entityField: '5baccdcb70aa490001802634',
                            operator: apiStatus,
                            type: 'criteria',
                        },
                        linkOperator: 'OR',
                        rightCriteria: null as any,
                        type: 'requestSearch',
                    }))
                    .reduce((fullQuery, queryPart) => ({ ...queryPart, rightCriteria: fullQuery })),
            ],
            undefined,
            undefined,
            undefined,
            undefined,
            excludedIds
        )
    }
}
